import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Modal, Form, Input, 
  message, Upload, Select, Tabs, Card, Typography, 
  Divider, Popconfirm, Row, Col, Spin, Tag, Alert,
  Dropdown, Menu, Checkbox
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, EditOutlined, 
  DownloadOutlined, UploadOutlined, ExclamationCircleOutlined,
  SyncOutlined, DownOutlined, ToolOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  normalizeScopeId,
  resolveDefaultSchoolId,
  isSchoolInAccessibleList,
  shouldLockSchoolSelection
} from '../../utils/schoolScope';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const Contacts = () => {
  const { currentUser } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classList, setClassList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [classBySchool, setClassBySchool] = useState({});
  const [grades, setGrades] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // 添加上传模态框状态
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCallbacks, setUploadCallbacks] = useState({onSuccess: null, onError: null});
  
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  
  // 添加新的状态
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteTargetType, setDeleteTargetType] = useState('class'); // 'class' 或 'grade'
  const [selectedDeleteClass, setSelectedDeleteClass] = useState(null);
  const [selectedDeleteGrade, setSelectedDeleteGrade] = useState(null);
  const [isFixingPhones, setIsFixingPhones] = useState(false);
  
  // 添加新的状态
  const [syncSelectedGrade, setSyncSelectedGrade] = useState(null);
  const [syncSelectedClass, setSyncSelectedClass] = useState(null);
  
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // 分享记录相关状态
  const [shareLogsVisible, setShareLogsVisible] = useState(false);
  const [shareLogs, setShareLogs] = useState([]);
  const [shareLogsLoading, setShareLogsLoading] = useState(false);
  
  // 表格行选择变更处理函数
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  
  // 获取分享记录
  const fetchShareLogs = async () => {
    if (!selectedSchool) {
      message.warning('请先选择学校');
      return;
    }
    
    setShareLogsLoading(true);
    try {
      const response = await api.get('/contacts/v2/share-logs', {
        params: { schoolId: selectedSchool }
      });
      
      if (response.data.success) {
        setShareLogs(response.data.data || []);
        setShareLogsVisible(true);
      } else {
        message.error(response.data.message || '获取分享记录失败');
      }
    } catch (error) {
      console.error('获取分享记录失败:', error);
      message.error('获取分享记录失败');
    } finally {
      setShareLogsLoading(false);
    }
  };
  
  // 批量删除选中的联系人
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条联系人进行删除');
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await api.post('/contacts/admin/batch-delete', {
        contactIds: selectedRowKeys
      });
      
      if (response.data.success) {
        message.success(`成功删除${selectedRowKeys.length}条联系人记录`);
        setSelectedRowKeys([]);
        fetchContacts();
      } else {
        message.error(response.data.message || '批量删除失败');
      }
    } catch (error) {
      console.error('批量删除联系人错误:', error);
      message.error('删除失败，请稍后再试');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // 获取学校列表
  const fetchSchools = async () => {
    try {
      const response = await api.get('/schools/list');
      if (response.data.success) {
        const schoolsData = response.data.data || [];
        setSchools(schoolsData);
        const savedSchoolId = localStorage.getItem('selectedSchoolId');
        const restoredSchoolId = normalizeScopeId(selectedSchool || savedSchoolId);
        const defaultSchoolId = isSchoolInAccessibleList(restoredSchoolId, schoolsData)
          ? restoredSchoolId
          : resolveDefaultSchoolId(currentUser, schoolsData);
        setSelectedSchool(defaultSchoolId);
        if (defaultSchoolId) {
          localStorage.setItem('selectedSchoolId', defaultSchoolId);
          fetchTeachers(defaultSchoolId);
          fetchClasses(defaultSchoolId);
        } else {
          localStorage.removeItem('selectedSchoolId');
          setTeachers([]);
          setClassList([]);
          setGrades([]);
        }
      } else {
        console.error('获取学校列表失败:', response.data.message);
      }
    } catch (error) {
      console.error('获取学校列表出错:', error);
    }
  };
  
  // 获取联系人列表 - 使用v2 API（数据共享模式）
  const fetchContacts = async () => {
    setLoading(true);
    try {
      console.log('获取联系人（v2 API），当前参数:', {
        选择的学校: selectedSchool, 
        选择的教师: selectedTeacher, 
        选择的班级: selectedClass,
        选择的年级: selectedGrade,
        用户角色: currentUser?.role
      });
      
      // 将当前选择的学校ID存储到localStorage
      if (selectedSchool) {
        localStorage.setItem('selectedSchoolId', selectedSchool);
        console.log('保存选中学校ID到localStorage:', selectedSchool);
      }
      
      // 根据用户角色选择不同的API端点
      let endpoint;
      let params = {};
      
      if (!selectedSchool) {
        setContacts([]);
        return;
      }

      params.schoolId = selectedSchool;
      if (currentUser.role === 'teacher') {
        // 教师使用v2/list API，只能看到有权限的通讯录
        endpoint = '/contacts/v2/list';
        console.log('教师用户，使用权限控制的API');
      } else {
        // 管理员使用admin/school API，可以看到所有通讯录
        endpoint = '/contacts/v2/admin/school';
        
      }
      
      // 必须有schoolId才能查询
      if (!params.schoolId) {
        console.log('缺少schoolId，无法查询');
        setContacts([]);
        setLoading(false);
        return;
      }
      
      // 如果指定了教师，获取该教师的通讯录
      if (selectedTeacher) {
        params.teacherId = selectedTeacher;
      }
      
      console.log(`获取联系人数据（v2），端点：${endpoint}，参数:`, params);
      const res = await api.get(endpoint, { params });
      
      if (res.data.success) {
        // 获取原始联系人数据
        let allContacts = res.data.contacts || [];
        console.log(`获取到${allContacts.length}条联系人数据`);
        
        // 首先尝试按班级筛选
        let filteredContacts = allContacts;
        
        if (selectedClass) {
          const selectedClassObj = classList.find(cls => cls.id === selectedClass);
          console.log('选中的班级对象:', selectedClassObj);
          
          if (selectedClassObj) {
            console.log(`按班级名称"${selectedClassObj.name}"筛选联系人`);
            console.log('所有联系人数据的班级字段:');
            allContacts.forEach(contact => {
              if (contact.class_name) {
                console.log(`学生 ${contact.student_name} 的班级字段: "${contact.class_name}"`);
              }
            });
            
            filteredContacts = allContacts.filter(contact => {
              // 如果班级名称为空，跳过匹配
              if (!contact.class_name) {
                console.log(`学生 ${contact.student_name || 'unknown'} 班级为空，跳过匹配`);
                return false;
              }
              
              // 转为字符串，防止比较类型不一致
              const contactClassName = String(contact.class_name).trim();
              const selectedClassName = String(selectedClassObj.name).trim();
              
              console.log(`比较班级: "${contactClassName}" 与 "${selectedClassName}"`);
              
              // 尝试多种匹配方式
              const exactMatch = contactClassName === selectedClassName;
              const containsMatch = contactClassName.includes(selectedClassName);
              const reverseContainsMatch = selectedClassName.includes(contactClassName);
              
              // 处理纯数字班级，如"701"与"701班"的匹配
              const cleanContactClass = contactClassName.replace(/班$/, '').trim();
              const cleanSelectedClass = selectedClassName.replace(/班$/, '').trim();
              const cleanMatch = cleanContactClass === cleanSelectedClass;
              
              const matches = exactMatch || containsMatch || reverseContainsMatch || cleanMatch;
              
              console.log(`${contact.student_name || 'unknown'}: 精确匹配=${exactMatch}, 包含匹配=${containsMatch}, 反向包含=${reverseContainsMatch}, 清理后匹配=${cleanMatch}, 最终结果=${matches}`);
              
              return matches;
            });
            console.log(`班级筛选后剩余${filteredContacts.length}条数据`);
          }
        }
        // 如果选择了年级但没选班级，按年级筛选
        else if (selectedGrade) {
          console.log(`按年级"${selectedGrade}"筛选联系人`);
          
          // 获取选定年级的所有班级
          const gradeClasses = classList
            .filter(cls => cls.grade === selectedGrade)
            .map(cls => cls.name);
            
          console.log('该年级下的班级列表:', gradeClasses);
          
          // 按年级筛选联系人
          filteredContacts = filteredContacts.filter(contact => {
            // 如果班级名称为空，跳过匹配
            if (!contact.class_name) return false;
            
            // 记录原始班级名称
            const className = contact.class_name;
            const matchingClasses = [];
            
            // 直接用年级名称进行匹配
            if (className.includes(selectedGrade)) {
              console.log(`联系人 ${contact.student_name} 班级 "${className}" 直接包含年级名称 "${selectedGrade}"`);
              return true;
            }
            
            // 检查是否在年级下的班级列表中
            for (const gradeCls of gradeClasses) {
              if (className === gradeCls || className.includes(gradeCls)) {
                matchingClasses.push(gradeCls);
              }
            }
            
            const matchesGrade = matchingClasses.length > 0;
            if (matchesGrade) {
              console.log(`联系人 ${contact.student_name} 班级 "${className}" 属于年级 "${selectedGrade}" 的班级: ${matchingClasses.join(', ')}`);
            }
            
            return matchesGrade;
          });
          
          console.log(`年级筛选后剩余${filteredContacts.length}条数据`);
        }
        
        // 如果有搜索关键词，进一步筛选
        if (searchKeyword) {
          const keyword = searchKeyword.toLowerCase();
          console.log(`按关键词"${keyword}"筛选联系人`);
          
          filteredContacts = filteredContacts.filter(contact => 
            (contact.student_name && contact.student_name.toLowerCase().includes(keyword)) ||
            (contact.phone1 && contact.phone1.includes(keyword)) ||
            (contact.phone2 && contact.phone2.includes(keyword))
          );
          
          console.log(`关键词筛选后剩余${filteredContacts.length}条数据`);
        }
        
        setContacts(filteredContacts);
      } else {
        message.error(res.data.message || '获取联系人列表失败');
        setContacts([]);
      }
    } catch (error) {
      console.error('获取联系人列表出错:', error);
      message.error('网络错误，请稍后再试');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 获取教师列表
  const fetchTeachers = async (schoolId) => {
    try {
      const id = normalizeScopeId(schoolId || selectedSchool);
      if (!id) {
        setTeachers([]);
        return;
      }
      
      const response = await api.get(`/schools/${id}/teachers`);
      if (response.data.success) {
        setTeachers(response.data.data || []);
        console.log('获取教师列表成功:', response.data.data);
      } else {
        console.error('获取教师列表失败:', response.data.message);
        setTeachers([]);
      }
    } catch (error) {
      console.error('获取教师列表出错:', error);
      setTeachers([]);
    }
  };
  
  // 获取班级列表和年级
  const fetchClasses = async (schoolId) => {
    try {
      const id = normalizeScopeId(schoolId || selectedSchool);
      if (!id) {
        setClassList([]);
        setGrades([]);
        return;
      }
      
      // 第一步：先获取联系人数据
      console.log('获取联系人数据以提取班级和年级:', `/contacts/admin/school/${id}`);
      
      const contactsResponse = await api.get(`/contacts/admin/school/${id}`);
      
      if (!contactsResponse.data.success) {
        console.error('获取联系人失败:', contactsResponse.data.message);
        setClassList([]);
        setGrades([]);
        return;
      }
      
      const contactsData = contactsResponse.data.contacts || [];
      console.log(`获取到${contactsData.length}条联系人数据用于提取班级和年级`);
        
      // 从联系人数据中提取班级和年级
      const classSet = new Set();
        const gradeSet = new Set();
      const classObjectList = [];
      let classIdCounter = 1;
      
      // 首先获取所有实际使用的班级名称
      contactsData.forEach(contact => {
        if (contact.class_name) {
          // 直接添加实际班级名称到集合中
          classSet.add(contact.class_name);
        }
      });
      
      console.log(`从联系人数据中提取的所有班级名称:`, Array.from(classSet));
      
      // 从班级名称中提取年级信息并创建班级对象
      Array.from(classSet).forEach(className => {
        console.log(`分析班级名称: "${className}"`);
        
        // 尝试从班级名称中提取年级
        let grade = "未知年级";
        let matched = false;
        
        // 常见的年级模式匹配
        const gradePatterns = [
          { regex: /(小学|初中|高中|小|初|高)([一二三四五六])(级|年级|年|\([\d]+\)班|\d+班)?/, extract: (m) => `${m[1]}${m[2]}级` },
          { regex: /([一二三四五六])年级/, extract: (m) => `${m[1]}年级` },
          { regex: /(一|二|三|四|五|六|七|八|九|十|十一|十二)年级/, extract: (m) => `${m[1]}年级` },
          { regex: /(一|二|三|四|五|六|七|八|九|十|十一|十二)(\d+)班/, extract: (m) => `${m[1]}年级` },
          { regex: /(\d+)年级/, extract: (m) => `${m[1]}年级` },
          { regex: /(初中|小学|高中)(\d+)年级/, extract: (m) => `${m[1]}${m[2]}年级` },
          { regex: /^初([一二三]).*$/, extract: (m) => `初${m[1]}级` },
          { regex: /^高([一二三]).*$/, extract: (m) => `高${m[1]}级` },
          { regex: /^([初高])([一二三]).*$/, extract: (m) => `${m[1]}${m[2]}级` },
          { regex: /^(\d)0\d班?$/, extract: (m) => `${parseInt(m[1])}年级` }
        ];
        
        // 检查班级名称中是否包含年级信息
        for (const pattern of gradePatterns) {
          const match = className.match(pattern.regex);
          if (match) {
            grade = pattern.extract(match);
            console.log(`班级"${className}"匹配模式，提取年级: "${grade}"`);
            matched = true;
            break;
          }
        }
        
        // 特殊处理数字班级，如701、702等
        if (!matched && /^\d+$/.test(className.trim())) {
          const classNum = className.trim();
          // 初中年级
          if (classNum.length === 3 && classNum.startsWith('7')) {
            grade = '7年级';
            matched = true;
          } else if (classNum.length === 3 && classNum.startsWith('8')) {
            grade = '8年级';
            matched = true;
          } else if (classNum.length === 3 && classNum.startsWith('9')) {
            grade = '9年级';
            matched = true;
          }
          // 高中年级
          else if (classNum.startsWith('10')) {
            grade = '10年级';
            matched = true;
          } else if (classNum.startsWith('11')) {
            grade = '11年级';
            matched = true;
          } else if (classNum.startsWith('12')) {
            grade = '12年级';
            matched = true;
          }
          
          if (matched) {
            console.log(`数字班级"${className}"提取年级: "${grade}"`);
          }
        }
        
        // 直接匹配班级号，例如"701班"、"702"等
        if (!matched) {
          const classNumMatch = className.match(/^(\d+)(?:班)?$/);
          if (classNumMatch) {
            const num = classNumMatch[1];
            if (num.startsWith('7')) {
              grade = '7年级';
              matched = true;
            } else if (num.startsWith('8')) {
              grade = '8年级';
              matched = true;
            } else if (num.startsWith('9')) {
              grade = '9年级';
              matched = true;
            } else if (num.startsWith('10')) {
              grade = '10年级';
              matched = true;
            } else if (num.startsWith('11')) {
              grade = '11年级';
              matched = true;
            } else if (num.startsWith('12')) {
              grade = '12年级';
              matched = true;
            }
            
            if (matched) {
              console.log(`班级号"${className}"提取年级: "${grade}"`);
            }
          }
        }
        
        // 如果没有匹配到年级，尝试使用数字前的内容作为年级
        if (!matched && grade === "未知年级") {
          const simpleMatch = className.match(/^(.*?)(\d+.*)$/);
          if (simpleMatch && simpleMatch[1]) {
            grade = simpleMatch[1].trim();
            console.log(`使用数字前内容作为年级: "${grade}"`);
            matched = true;
          }
        }
        
        // 添加到年级集合
        if (grade && grade !== "未知年级") {
          gradeSet.add(grade);
        } else {
          // 如果真的无法提取年级，将其分配到"未分类"
          grade = "未分类年级";
          gradeSet.add(grade);
          console.log(`无法从"${className}"提取年级，归类为"未分类年级"`);
        }
        
        // 创建班级对象
        const classObj = {
          id: classIdCounter++,
          name: className,  // 使用实际班级名称
          grade: grade,
          school_id: id
        };
        
        classObjectList.push(classObj);
        console.log(`添加班级对象: ID=${classObj.id}, 名称="${classObj.name}", 年级="${classObj.grade}"`);
      });
      
      setClassList(classObjectList);
      console.log('从联系人数据中提取的班级列表:', classObjectList);
      
      // 设置年级列表
      const gradesArray = Array.from(gradeSet).sort();
      setGrades(gradesArray);
      console.log('从联系人数据中提取的年级列表:', gradesArray);
        
        // 按班级分组
        const classMap = {};
      classObjectList.forEach(cls => {
          classMap[cls.id] = cls.name;
        });
        
        setClassBySchool(classMap);
    } catch (error) {
      console.error('获取班级和年级数据出错:', error);
      setClassList([]);
      setGrades([]);
    }
  };
  
  // 当选择学校时
  const handleSchoolChange = (value) => {
    setSelectedSchool(value);
    setSelectedTeacher(null); // 重置教师选择
    setSelectedClass(null); // 重置班级选择
    setSelectedGrade(null); // 重置年级选择
    
    // 重新获取教师和班级列表
    fetchTeachers(value);
    fetchClasses(value);
  };
  
  // 分析联系人数据，提取出所有的班级
  const getClassesFromContacts = () => {
    const classes = new Set();
    contacts.forEach(contact => {
      if (contact.class_name) {
        classes.add(contact.class_name);
      }
    });
    return Array.from(classes).sort();
  };
  
  // 组件初始化时，添加调试日志
  useEffect(() => {
    console.log('通讯录组件初始化');
    console.log('当前用户信息:', currentUser);
    console.log('学校列表状态:', schools);
    console.log('当前选择的学校ID:', selectedSchool);
    
    if (currentUser) {
      console.log('用户详细信息:');
      console.log('- 用户ID:', currentUser.id, '(类型:', typeof currentUser.id, ')');
      console.log('- 用户角色:', currentUser.role);
      console.log('- 学校ID:', currentUser.schoolId, '(类型:', typeof currentUser.schoolId, ')');
    }
    
    if (currentUser) fetchSchools();
    
    // 组件首次挂载时执行一次
  }, []);
  
  // 当学校选择发生变化时，获取相关数据
  useEffect(() => {
    console.log('学校或教师选择变化，重新获取数据');
    if (currentUser) {
      fetchTeachers();
      fetchClasses();
    }
  }, [currentUser, selectedSchool]);
  
  useEffect(() => {
    console.log('需要获取联系人列表，参数:', {
      学校: selectedSchool,
      教师: selectedTeacher,
      班级: selectedClass,
      年级: selectedGrade
    });
    if (currentUser) {
      fetchContacts();
    }
  }, [currentUser, selectedTeacher, selectedSchool, selectedClass, selectedGrade]);
  
  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
  };
  
  // 处理添加联系人
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      console.log('表单数据:', values);
      
      setLoading(true);
      
      if (selectedContact) {
        // 编辑联系人 - 使用v2 API
        const res = await api.put(`/contacts/v2/admin/${selectedContact.id}`, {
          student_name: values.studentName,
          class_name: values.className,
          parent1_name: values.parent1Name,
          phone1: values.phone1,
          parent2_name: values.parent2Name,
          phone2: values.phone2,
          address: values.address
        });
        
        if (res.data.success) {
          message.success('联系人更新成功');
          setIsModalVisible(false);
          fetchContacts();
        } else {
          message.error(res.data.message || '更新联系人失败');
        }
      } else {
        // 添加联系人
        let teacherId = values.teacherId;
        
        // 如果当前用户是教师，使用当前教师ID
        if (currentUser && currentUser.role === 'teacher') {
          teacherId = currentUser?.id;
        }
        
        // 默认使用选择的学校ID或当前用户的学校ID
        let schoolId = selectedSchool || (currentUser && currentUser.schoolId) || '';
        
        const postData = {
          student_name: values.studentName,
          studentId: values.studentId || '',
          gender: values.gender || '',
          className: values.className || '',
          idCard: values.idCard || '',
          parent1Name: values.parent1Name || '',
          phone1: values.phone1 || '',
          parent2Name: values.parent2Name || '',
          phone2: values.phone2 || '',
          address: values.address || '',
          schoolId: schoolId
        };
        
        // 如果有教师ID，添加到请求数据中
        if (teacherId) {
          postData.teacherId = teacherId;
        }
        
        console.log('准备提交数据:', postData);
        
        try {
          const res = await api.post('/contacts/v2/admin/create', {
            student_name: values.studentName,
            class_name: values.className || '',
            parent1_name: values.parent1Name || '',
            phone1: values.phone1 || '',
            parent2_name: values.parent2Name || '',
            phone2: values.phone2 || '',
            address: values.address || '',
            school_id: schoolId,
            teacher_id: teacherId
          });
          
          if (res.data.success) {
            message.success('联系人添加成功');
            setIsModalVisible(false);
            fetchContacts();
          } else {
            message.error(res.data.message || '添加联系人失败');
          }
        } catch (error) {
          console.error('提交联系人数据错误:', error.response?.data || error.message);
          message.error(error.response?.data?.message || '添加联系人失败，请检查填写的信息');
        }
      }
    } catch (error) {
      console.error('提交联系人表单出错:', error);
      message.error('表单验证失败，请检查填写的信息');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理删除联系人
  const handleDelete = async (record) => {
    setLoading(true);
    try {
      const res = await api.delete(`/contacts/v2/admin/${record.id}`);
      
      if (res.data.success) {
        message.success('联系人删除成功');
        fetchContacts();
      } else {
        message.error(res.data.message || '删除联系人失败');
      }
    } catch (error) {
      console.error('删除联系人出错:', error);
      message.error('删除失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理教师选择
  const handleTeacherSelect = (teacherId) => {
    console.log('选择教师:', teacherId);
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };
  
  // 处理批量同步 - 修改为支持按年级/班级筛选
  const handleBatchSync = async () => {
    if (selectedTeachers.length === 0) {
      message.warning('请选择至少一位教师');
      return;
    }

    if (!selectedSchool) {
      message.warning('请先选择学校');
      return;
    }

    setLoading(true);
    // 显示进度提示
    message.loading({
      content: '正在同步数据，请稍候...',
      key: 'syncProgress',
      duration: 0
    });

    try {
      console.log('开始分享通讯录，参数:', {
        目标教师IDs: selectedTeachers,
        学校ID: selectedSchool,
        年级筛选: syncSelectedGrade,
        班级筛选: syncSelectedClass
      });

      let successCount = 0;
      let skipCount = 0;
      let failCount = 0;

      // 获取要分享的联系人数据
      let contactsToShare = [];
      let filterInfo = '';
      
      // 使用v2 API获取联系人
      console.log(`获取学校ID=${selectedSchool}的联系人数据`);
      const contactsResponse = await api.get('/contacts/v2/admin/school', {
        params: { schoolId: selectedSchool }
      });
      
      if (contactsResponse.data.success) {
        let allContacts = contactsResponse.data.contacts || [];
        contactsToShare = allContacts;
        
        // 如果选择了班级进行筛选
        if (syncSelectedClass) {
          const classObj = classList.find(cls => cls.id === syncSelectedClass);
          if (classObj) {
            console.log(`分享前按班级"${classObj.name}"筛选联系人`);
            contactsToShare = allContacts.filter(contact => {
              if (!contact.class_name) return false;
              
              const contactClass = String(contact.class_name).trim();
              const selectedClass = String(classObj.name).trim();
              
              // 使用相同的匹配逻辑
              const exactMatch = contactClass === selectedClass;
              const containsMatch = contactClass.includes(selectedClass);
              const reverseContainsMatch = selectedClass.includes(contactClass);
              const cleanContactClass = contactClass.replace(/班$/, '').trim();
              const cleanSelectedClass = selectedClass.replace(/班$/, '').trim();
              const cleanMatch = cleanContactClass === cleanSelectedClass;
              
              return exactMatch || containsMatch || reverseContainsMatch || cleanMatch;
            });
            filterInfo = `班级"${classObj.name}"`;
          }
        }
        // 如果选择了年级进行筛选
        else if (syncSelectedGrade) {
          console.log(`分享前按年级"${syncSelectedGrade}"筛选联系人`);
          
          // 获取选定年级的所有班级
          const gradeClasses = classList
            .filter(cls => cls.grade === syncSelectedGrade)
            .map(cls => cls.name);
            
          contactsToShare = allContacts.filter(contact => {
            if (!contact.class_name) return false;
            
            // 直接用年级名称进行匹配
            if (contact.class_name.includes(syncSelectedGrade)) {
              return true;
            }
            
            // 检查是否在年级下的班级列表中
            for (const gradeCls of gradeClasses) {
              if (contact.class_name === gradeCls || contact.class_name.includes(gradeCls)) {
                return true;
              }
            }
            
            return false;
          });
          
          filterInfo = `年级"${syncSelectedGrade}"`;
        }
        
        console.log(`筛选后将分享${contactsToShare.length}条联系人数据`);
        
        if (contactsToShare.length === 0) {
          message.info({
            content: `筛选的${filterInfo}没有找到联系人数据，无法分享`,
            key: 'syncProgress'
          });
          setLoading(false);
          return;
        }
      } else {
        message.error({
          content: '获取联系人数据失败，无法分享',
          key: 'syncProgress'
        });
        setLoading(false);
        return;
      }
      
      // 获取联系人ID列表
      const contactIds = contactsToShare.map(contact => contact.id);

      // 使用v2 API批量分享
      try {
        console.log(`开始批量分享，教师数量: ${selectedTeachers.length}, 联系人数量: ${contactIds.length}`);
        
        const response = await api.post('/contacts/v2/share', {
          targetTeacherIds: selectedTeachers.map(id => parseInt(id)),
          contactIds: contactIds
        });

        console.log('批量分享响应:', response.data);

        if (response.data.success) {
          successCount = contactIds.length * selectedTeachers.length;
          skipCount = 0;
          failCount = 0;
          
          message.success({
            content: `成功分享${contactIds.length}条通讯录给${selectedTeachers.length}位教师`,
            key: 'syncProgress'
          });
        } else {
          message.error({
            content: response.data.message || '批量分享失败',
            key: 'syncProgress'
          });
        }
      } catch (error) {
        console.error('批量分享请求出错:', error);
        message.error({
          content: '批量分享失败: ' + (error.response?.data?.message || error.message),
          key: 'syncProgress'
        });
        setLoading(false);
        return;
      }

      // 更新提示信息
      const filterDesc = filterInfo ? `(${filterInfo}的)` : '';
      const teacherCount = selectedTeachers.length;
      
      if (successCount > 0) {
        message.success({
          content: `分享完成：成功将${filterDesc}${contactsToShare.length}条记录分享给${teacherCount}位教师`,
          key: 'syncProgress'
        });
      } else if (skipCount > 0 && successCount === 0) {
        message.info({
          content: `所有${filterDesc}数据都已同步，跳过${skipCount}条重复记录`,
          key: 'syncProgress'
        });
      } else if (failCount > 0 && successCount === 0) {
        message.error({
          content: `同步失败，${failCount}个教师同步失败`,
          key: 'syncProgress'
        });
      } else {
        message.info({
          content: '没有可同步的数据',
          key: 'syncProgress'
        });
      }

      // 刷新数据
      fetchContacts();
    } catch (error) {
      console.error('批量同步出错:', error);
      message.error({
        content: '同步失败: ' + (error.response?.data?.message || error.message),
        key: 'syncProgress'
      });
    } finally {
      setLoading(false);
      setSelectedTeachers([]); // 重置选择
      // 保留筛选条件，便于继续同步
    }
  };
  
  // 修复下载Excel模板功能
  const handleDownloadTemplate = (type = 'default') => {
    try {
      // 使用配置的API baseURL
      window.location.href = `${api.defaults.baseURL}/contacts/template/noauth`;
      
      message.success('正在下载模板文件...');
    } catch (error) {
      console.error('下载模板出错:', error);
      message.error('下载模板失败，请稍后再试');
    }
  };
  
  // 处理导入Excel
  const handleImport = async (options) => {
    const { file, onSuccess, onError } = options;
    
    // 保存上传文件和回调函数
    setUploadFile(file);
    setUploadCallbacks({ onSuccess, onError });
    
    // 显示上传对话框
    setIsUploadModalVisible(true);
    
    // 返回一个空对象以避免Upload组件报错
    return {
      abort: () => {}
    };
  };
  
  // 处理确认上传
  const handleUploadConfirm = async () => {
    if (!uploadFile || !uploadCallbacks.onSuccess || !uploadCallbacks.onError) {
      message.error('上传配置错误');
      return;
    }
    
    setLoading(true);
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    // 获取认证TOKEN
    const token = localStorage.getItem('token');
    if (!token) {
      message.error('未登录或登录已过期');
      setLoading(false);
      return;
    }
    
    formData.append('token', token);
    
    // 添加上传前日志
    console.log('上传数据信息:', {
      用户角色: currentUser?.role,
      选择的学校ID: selectedSchool,
      用户默认学校ID: currentUser?.schoolId,
      学校列表长度: schools?.length
    });
    
    const uploadSchoolId = normalizeScopeId(selectedSchool);
    
    if (!uploadSchoolId) {
      message.error('请先选择一个学校后再上传');
      setLoading(false);
      return;
    }
    
    // 查找学校对象以获取名称
    const selectedSchoolObj = schools.find(s => Number(s.id) === Number(uploadSchoolId));
    if (!selectedSchoolObj) {
      message.error('所选学校无效，请重新选择');
      setLoading(false);
      return;
    }
    
    console.log('即将上传到学校:', selectedSchoolObj.name, '(ID:', uploadSchoolId, ')');
    formData.append('schoolId', uploadSchoolId);
    
    try {
      console.log('开始上传联系人数据:', formData);
     
      // 使用v2 API导入（数据共享模式）
       const res = await axios.post(`${api.defaults.baseURL}/contacts/v2/import`, formData, {
         headers: {
           'Content-Type': 'multipart/form-data',
           'Authorization': token ? `Bearer ${token}` : ''
         }
       });
      
      if (res.data.success) {
        message.success(`成功导入 ${res.data.importedCount} 条联系人数据`);
        if (res.data.errorCount > 0) {
          message.warning(`有 ${res.data.errorCount} 条数据导入失败`);
        }
        
        // 确保上传后选择正确的学校
        console.log('上传成功，更新当前选择的学校ID:', uploadSchoolId);
        setSelectedSchool(uploadSchoolId);
        
        // 使用短暂延迟确保模态框关闭后再刷新数据，防止状态冲突
        setTimeout(() => {
          fetchContacts();
        }, 300);
        
        uploadCallbacks.onSuccess();
        setIsUploadModalVisible(false);
      } else {
        message.error(res.data.message || '导入失败');
        uploadCallbacks.onError('导入失败');
      }
    } catch (error) {
      console.error('导入Excel出错:', error);
      message.error('导入失败，请稍后再试');
      uploadCallbacks.onError('导入失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理添加联系人（打开表单）
  const handleAddContact = () => {
    setSelectedContact(null);
    form.resetFields();
    
    // 初始化表单默认值
    const initialValues = {
      studentName: '',
      studentId: '',
      gender: '',
      className: '',
      schoolName: '',
      idCard: '',
      parent1Name: '',
      phone1: '',
      parent2Name: '',
      phone2: '',
      address: ''
    };
    
    // 若有已选教师，设为默认
    if (selectedTeacher) {
      initialValues.teacherId = selectedTeacher;
    }
    
    // 若有已选学校，设为默认
    if (selectedSchool) {
      const selectedSchoolObj = schools.find(s => s.id === selectedSchool);
      if (selectedSchoolObj) {
        initialValues.schoolName = selectedSchoolObj.name;
      }
    }
    
    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
  };
  
  // 按班级删除联系人
  const handleBatchDeleteByClass = async () => {
    if (!selectedDeleteClass) {
      message.error('请选择要删除的班级');
      return;
    }
    
    try {
      // 找到对应的班级对象
      const classInfo = classList.find(cls => cls.id === selectedDeleteClass);
      if (!classInfo) {
        message.error('未找到对应的班级信息');
        return;
      }
      
      console.log('准备删除班级:', classInfo);
      
      let endpoint = `/contacts/admin/delete-by-class`;
      const response = await api.post(endpoint, {
        className: classInfo.name,  // 确保传递的是班级名称字符串而非ID
        schoolId: selectedSchool
      });
      
      console.log('删除班级响应:', response.data);
      
      if (response.data.success) {
        message.success(response.data.message || '成功删除班级联系人');
        // 刷新联系人列表
        fetchContacts();
        setIsDeleteModalVisible(false);
      } else {
        message.error(response.data.message || '删除班级联系人失败');
      }
    } catch (error) {
      console.error('删除班级联系人错误:', error.response?.data || error);
      message.error('删除班级联系人失败，请稍后再试');
    }
  };
  
  // 按年级删除联系人
  const handleBatchDeleteByGrade = async () => {
    if (!selectedDeleteGrade) {
      message.error('请选择要删除的年级');
      return;
    }
    
    if (!selectedSchool) {
      message.error('请先选择学校');
      return;
    }
    
    try {
      console.log('准备删除年级:', selectedDeleteGrade);
      
      // 不需要从班级管理中获取班级列表，直接使用年级名称操作
      let endpoint = `/contacts/admin/delete-by-grade`;
      const response = await api.post(endpoint, {
        gradeName: selectedDeleteGrade,  
        schoolId: selectedSchool
      });
      
      console.log('删除年级响应:', response.data);
      
      if (response.data.success) {
        message.success(response.data.message || '成功删除年级联系人');
        // 刷新联系人列表
        fetchContacts();
        setIsDeleteModalVisible(false);
      } else {
        message.error(response.data.message || '删除年级联系人失败');
      }
    } catch (error) {
      console.error('删除年级联系人错误:', error.response?.data || error);
      message.error('删除年级联系人失败，请稍后再试');
    }
  };
  
  // 修复电话号码
  const handleFixPhoneNumbers = async () => {
    if (!selectedSchool) {
      message.warning('请先选择学校');
      return;
    }
    
    setIsFixingPhones(true);
    
    try {
      console.log('开始修复电话号码格式');
      const res = await api.post('/contacts/admin/fix-phone-numbers', {
        schoolId: selectedSchool
      });
      
      if (res.data.success) {
        if (res.data.fixedCount > 0) {
          message.success(res.data.message || `成功修复${res.data.fixedCount}个电话号码`);
          fetchContacts(); // 刷新数据
        } else {
          message.info(res.data.message || '没有需要修复的电话号码');
        }
      } else {
        message.error(res.data.message || '修复电话号码失败');
      }
    } catch (error) {
      console.error('修复电话号码出错:', error);
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setIsFixingPhones(false);
    }
  };
  
  // 显示删除模态框
  const showDeleteModal = (type) => {
    setDeleteTargetType(type);
    
    // 如果当前已经筛选了班级，默认选中这个班级
    if (type === 'class' && selectedClass) {
      setSelectedDeleteClass(selectedClass);
    }
    // 如果当前已经筛选了年级，默认选中这个年级
    else if (type === 'grade' && selectedGrade) {
      setSelectedDeleteGrade(selectedGrade);
    }
    
    setIsDeleteModalVisible(true);
  };
  
  // 表格列定义
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
    },
    {
      title: '学籍号',
      dataIndex: 'student_id',
      key: 'student_id',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
      render: (text) => text || '-'
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
      render: (text) => text || '未分班'
    },
    {
      title: '学校',
      key: 'school_name',
      width: 150,
      render: (_, record) => {
        const schoolId = record.school_id;
        if (schools.length > 0) {
          return schools.find(s => s.id === schoolId)?.name || '未知学校';
        }
        return schoolId || '未知学校';
      }
    },
    {
      title: '家长姓名1',
      dataIndex: 'parent1_name',
      key: 'parent1_name',
      width: 100,
      render: (text) => text || '-'
    },
    {
      title: '联系电话1',
      dataIndex: 'phone1',
      key: 'phone1',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '家长姓名2',
      dataIndex: 'parent2_name',
      key: 'parent2_name',
      width: 100,
      render: (text) => text || '-'
    },
    {
      title: '联系电话2',
      dataIndex: 'phone2',
      key: 'phone2',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '身份证号',
      dataIndex: 'id_card',
      key: 'id_card',
      width: 150,
      render: (text) => text ? text.replace(/^(.{6})(.*)(.{4})$/, '$1********$3') : '-'
    },
    {
      title: '教师',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
      width: 100,
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => {
              setSelectedContact(record);
              form.setFieldsValue({
                studentName: record.student_name,
                studentId: record.student_id,
                gender: record.gender,
                className: record.class_name,
                idCard: record.id_card,
                parent1Name: record.parent1_name,
                phone1: record.phone1,
                parent2Name: record.parent2_name,
                phone2: record.phone2,
                address: record.address,
                teacherId: record.teacher_id
              });
              setIsModalVisible(true);
            }}
          />
          <Popconfirm
            title="确定要删除此联系人吗？"
            onConfirm={() => handleDelete(record)}
            okText="是"
            cancelText="否"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  
  // 渲染筛选工具栏
  const renderFilterToolbar = () => {
    return (
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          {/* 第一行：学校和搜索 */}
          <Col xs={24} sm={8} md={6} lg={5} xl={4}>
            <Form.Item label="选择学校" style={{ marginBottom: 8 }}>
              <Select
                placeholder="选择学校"
                value={selectedSchool}
                onChange={handleSchoolChange}
                style={{ width: '100%' }}
                loading={loading}
                disabled={shouldLockSchoolSelection(currentUser, schools)}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {schools.map(school => (
                  <Option key={school.id} value={normalizeScopeId(school.id)}>
                    {school.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={6}>
            <Input.Search
              placeholder="搜索姓名或电话号码"
              allowClear
              enterButton
              onSearch={handleSearch}
              onChange={(e) => !e.target.value && setSearchKeyword('')}
            />
          </Col>
          
          {/* 确保任何用户都能看到并选择学校 */}
          <Col span={3}>
            <Select
              placeholder="选择年级"
              style={{ width: '100%' }}
              onChange={(value) => {
                setSelectedGrade(value);
                setSelectedClass(null);
              }}
              value={selectedGrade}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {grades.map(grade => (
                <Option key={grade} value={grade}>{grade}</Option>
              ))}
            </Select>
          </Col>
          
          <Col span={3}>
            <Select
              placeholder="选择班级"
              style={{ width: '100%' }}
              onChange={setSelectedClass}
              value={selectedClass}
              allowClear
              disabled={!selectedSchool}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {classList
                .filter(cls => !selectedGrade || cls.grade === selectedGrade)
                .map(cls => (
                  <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                ))
              }
            </Select>
          </Col>
          
          <Col span={3}>
            <Select
              placeholder="选择教师"
              style={{ width: '100%' }}
              onChange={setSelectedTeacher}
              value={selectedTeacher}
              allowClear
              disabled={!selectedSchool}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {teachers.map(teacher => (
                <Option key={teacher.id} value={teacher.id}>{teacher.name}</Option>
              ))}
            </Select>
          </Col>
          
          {/* 批量操作按钮 */}
          <Col xs={24} sm={8} md={6} lg={5} xl={4}>
            <Form.Item label=" " style={{ marginBottom: 8 }}>
              <Space>
                <Popconfirm
                  title="确定要删除选中的联系人吗？"
                  onConfirm={handleBatchDelete}
                  okText="是"
                  cancelText="否"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    disabled={selectedRowKeys.length === 0}
                    loading={deleteLoading}
                  >
                    删除选中的 {selectedRowKeys.length} 项
                  </Button>
                </Popconfirm>
                
                <Dropdown overlay={
                  <Menu>
                    <Menu.Item key="deleteByClass" onClick={() => showDeleteModal('class')}>
                      <DeleteOutlined /> 按班级批量删除
                    </Menu.Item>
                    <Menu.Item key="deleteByGrade" onClick={() => showDeleteModal('grade')}>
                      <DeleteOutlined /> 按年级批量删除
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item key="fixPhoneNumbers" onClick={handleFixPhoneNumbers}>
                      <ToolOutlined /> 修复电话号码格式
                    </Menu.Item>
                  </Menu>
                }>
                  <Button>
                    批量操作 <DownOutlined />
                  </Button>
                </Dropdown>
              </Space>
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={8} md={6} lg={5} xl={4} style={{ textAlign: 'right' }}>
            <Form.Item label=" " style={{ marginBottom: 8 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddContact}
                >
                  添加联系人
                </Button>
                
                {currentUser && (currentUser.role === 'super_admin' || currentUser.role === 'school_admin') && (
                  <Button onClick={fetchShareLogs} loading={shareLogsLoading}>
                    查看分享记录
                  </Button>
                )}
                
                <Upload
                  name="file"
                  showUploadList={false}
                  customRequest={handleImport}
                  beforeUpload={beforeUpload}
                >
                  <Button icon={<UploadOutlined />}>导入</Button>
                </Upload>
                
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadTemplate()}
                >
                  下载模板
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
        
        {/* 第二行：其他过滤器 */}
        <Row gutter={16}>
          {/* ... existing filters ... */}
        </Row>
      </Card>
    );
  };
  
  // 添加一个函数来处理文件上传错误
  const beforeUpload = (file) => {
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                   file.type === 'application/vnd.ms-excel';
    if (!isExcel) {
      message.error('只能上传Excel文件!');
      return Upload.LIST_IGNORE;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('文件必须小于2MB!');
      return Upload.LIST_IGNORE;
    }
    
    return true;
  };
  
  // 渲染上传对话框
  const renderUploadModal = () => {
    const selectedSchoolObj = schools.find(
      school => normalizeScopeId(school.id) === normalizeScopeId(selectedSchool)
    );

    return (
      <Modal
        title={<><UploadOutlined /> 请选择上传学校</>}
        visible={isUploadModalVisible}
        onOk={handleUploadConfirm}
        onCancel={() => {
          setIsUploadModalVisible(false);
          if (uploadCallbacks.onError) {
            uploadCallbacks.onError('用户取消上传');
          }
        }}
        confirmLoading={loading}
        width={600}
        okText="确认上传"
        cancelText="取消"
      >
        <div style={{ padding: '20px 0' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Alert
                message="注意"
                description="只能上传到当前账号有权访问的学校，请确认学校后再导入。"
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            </Col>
            <Col span={24}>
              <Form.Item label="选择学校" required>
                <Select
                  placeholder="请选择学校(必选)"
                  style={{ width: '100%' }}
                  value={selectedSchool}
                  disabled={shouldLockSchoolSelection(currentUser, schools)}
                  onChange={handleSchoolChange}
                >
                  {schools && schools.length > 0 ? (
                    schools.map(school => (
                      <Option key={school.id} value={normalizeScopeId(school.id)}>{school.name}</Option>
                    ))
                  ) : (
                    <Option value="" disabled>暂无学校数据</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Alert
                message="重要提示"
                description="请确保已选择正确的学校！上传的通讯录将导入到选定的学校中。请在上传前核对Excel中的学校名称与系统中的学校名称是否一致。"
                type="warning"
                showIcon
              />
            </Col>
            {selectedSchool && (
              <Col span={24}>
                <Alert
                  message="已选择学校"
                  description={`选中的学校: ${selectedSchoolObj?.name || '加载中...'} (ID: ${selectedSchool})`}
                  type="success"
                  showIcon
                />
              </Col>
            )}
          </Row>
        </div>
      </Modal>
    );
  };
  
  // 渲染同步标签页内容
  const renderSyncTab = () => (
    <Card>
      <Title level={4}>通讯录分享</Title>
      <Text>将学校通讯录数据分享给选中的教师（不会复制数据，只授权访问）</Text>
      <Divider />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="步骤1：选择学校" size="small">
            <Select
              placeholder="选择学校"
              style={{ width: '100%' }}
              onChange={handleSchoolChange}
              value={selectedSchool}
            >
              {schools.map(school => (
                <Option key={school.id} value={school.id}>{school.name}</Option>
              ))}
            </Select>
          </Card>
        </Col>
        
        {/* 添加年级和班级筛选 */}
        <Col span={24}>
          <Card title="步骤2：筛选要分享的联系人数据（可选）" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="按年级筛选">
                  <Select
                    placeholder="选择年级（可选）"
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      setSyncSelectedGrade(value);
                      setSyncSelectedClass(null); // 清除班级选择
                    }}
                    value={syncSelectedGrade}
                    allowClear
                  >
                    {grades.map(grade => (
                      <Option key={grade} value={grade}>{grade}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="按班级筛选">
                  <Select
                    placeholder="选择班级（可选）"
                    style={{ width: '100%' }}
                    onChange={setSyncSelectedClass}
                    value={syncSelectedClass}
                    allowClear
                    disabled={!selectedSchool}
                  >
                    {classList
                      .filter(cls => !syncSelectedGrade || cls.grade === syncSelectedGrade)
                      .map(cls => (
                        <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Alert
              message="提示"
              description={
                syncSelectedClass ? 
                  `将同步班级"${classList.find(c => c.id === syncSelectedClass)?.name || ''}"的联系人数据` :
                  syncSelectedGrade ? 
                    `将同步${syncSelectedGrade}的所有联系人数据` : 
                    '未选择年级或班级，将同步所有联系人数据'
              }
              type="info"
              showIcon
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card 
            title="步骤3：选择要分享给的教师" 
            size="small"
            extra={
              <Space>
                <Tag color="blue">
                  已选择: {selectedTeachers.length} 位教师
                </Tag>
                <Button
                  type="primary"
                  onClick={handleBatchSync}
                  disabled={selectedTeachers.length === 0 || !selectedSchool}
                  loading={loading}
                >
                  开始分享
                </Button>
              </Space>
            }
          >
            <Row gutter={[8, 8]}>
              {teachers.map(teacher => (
                <Col span={6} key={teacher.id}>
                  <Card size="small" hoverable>
                    <Checkbox
                      checked={selectedTeachers.includes(teacher.id)}
                      onChange={() => handleTeacherSelect(teacher.id)}
                    >
                      <Space>
                        {teacher.name}
                        <Tag color="default">{teacher.subject || '未设置科目'}</Tag>
                      </Space>
                    </Checkbox>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </Card>
  );
  
  // 渲染删除模态框
  const renderDeleteModal = () => (
    <Modal
      title={deleteTargetType === 'class' ? "按班级批量删除" : "按年级批量删除"}
      open={isDeleteModalVisible}
      onCancel={() => setIsDeleteModalVisible(false)}
      onOk={deleteTargetType === 'class' ? handleBatchDeleteByClass : handleBatchDeleteByGrade}
      okText="确认删除"
      cancelText="取消"
      okButtonProps={{ danger: true }}
    >
      <Alert
        message="警告"
        description={`此操作将删除所选${deleteTargetType === 'class' ? '班级' : '年级'}的所有联系人，无法恢复！请确认操作。`}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      {deleteTargetType === 'class' ? (
        // 按班级删除
        <Form layout="vertical">
          <Form.Item label="选择班级">
            <Select
              placeholder="请选择要删除的班级"
              style={{ width: '100%' }}
              value={selectedDeleteClass}
              onChange={value => setSelectedDeleteClass(value)}
            >
              {classList.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      ) : (
        // 按年级删除
        <Form layout="vertical">
          <Form.Item label="选择年级">
            <Select
              placeholder="请选择要删除的年级"
              style={{ width: '100%' }}
              value={selectedDeleteGrade}
              onChange={value => setSelectedDeleteGrade(value)}
            >
              {grades.map(grade => (
                <Option key={grade} value={grade}>
                  {grade}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
  
  // 当删除模态框打开时，获取班级和年级数据
  useEffect(() => {
    if (isDeleteModalVisible) {
      console.log('删除模态框打开，获取班级和年级数据');
      // 使用当前选择的学校或用户默认学校
      const schoolId = selectedSchool || (currentUser && currentUser.schoolId);
      if (schoolId) {
        fetchClasses(schoolId);
      } else if (currentUser && currentUser.role === 'super_admin') {
        // 超级管理员可以获取所有学校的班级数据
        fetchClasses();
      }
    }
  }, [isDeleteModalVisible, selectedSchool, currentUser]);
  
  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    // 添加checkbox列的标题
    columnTitle: ' ',
  };
  
  return (
    <div style={{ padding: 24 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="通讯录管理" key="1">
          <Card>
            {renderFilterToolbar()}
            
            <Spin spinning={loading}>
              {!selectedSchool && currentUser?.role === 'super_admin' ? (
                <Alert
                  message="请选择学校"
                  description="超级管理员需要先在上方选择一个学校，才能查看该学校的通讯录数据。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              ) : null}
              <Table 
                columns={columns} 
                dataSource={contacts} 
                rowKey="id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1300 }}
                size="middle"
                rowSelection={rowSelection}
              />
            </Spin>
          </Card>
        </TabPane>
        
        <TabPane tab="通讯录分享" key="2">
          {renderSyncTab()}
        </TabPane>
      </Tabs>
      
      {/* 添加/编辑联系人表单 */}
      <Modal
        title={selectedContact ? '编辑联系人' : '添加联系人'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="studentName"
                label="学生姓名"
                rules={[{ required: true, message: '请输入学生姓名' }]}
              >
                <Input placeholder="请输入学生姓名" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="studentId"
                label="学籍号"
              >
                <Input placeholder="请输入学籍号" maxLength={20} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="gender"
                label="性别"
              >
                <Select placeholder="请选择性别">
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="className"
                label="班级"
              >
                <Select placeholder="请选择班级" allowClear>
                  {classList.map(cls => (
                    <Option key={cls.id} value={cls.name}>{cls.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="schoolName"
                label="学校名称"
              >
                <Input placeholder="请输入学校名称" maxLength={30} disabled={!!selectedSchool} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="idCard"
                label="身份证号"
                rules={[
                  { 
                    pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/, 
                    message: '请输入有效的身份证号码', 
                    validateTrigger: 'onBlur' 
                  }
                ]}
              >
                <Input placeholder="请输入身份证号" maxLength={18} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parent1Name"
                label="家长姓名1"
              >
                <Input placeholder="请输入家长姓名" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone1"
                label="联系电话1"
                rules={[
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
                ]}
              >
                <Input placeholder="请输入联系电话" maxLength={11} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parent2Name"
                label="家长姓名2"
              >
                <Input placeholder="请输入家长姓名" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone2"
                label="联系电话2"
                rules={[
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
                ]}
              >
                <Input placeholder="请输入联系电话" maxLength={11} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="address"
            label="家庭住址"
          >
            <Input.TextArea rows={3} placeholder="请输入家庭住址" maxLength={100} />
          </Form.Item>
          
          {!selectedContact && currentUser && currentUser.role !== 'teacher' && (
            <Form.Item
              name="teacherId"
              label="选择教师"
              rules={[]}
              initialValue={selectedTeacher}
            >
              <Select placeholder="请选择教师" allowClear>
                {teachers.map(teacher => (
                  <Option key={teacher.id} value={teacher.id}>{teacher.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
      
      {renderUploadModal()}
      
      {/* 添加删除模态框 */}
      {renderDeleteModal()}
      
      {/* 分享记录模态框 */}
      <Modal
        title="通讯录分享记录"
        visible={shareLogsVisible}
        onCancel={() => setShareLogsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShareLogsVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        <Table
          dataSource={shareLogs}
          rowKey="id"
          loading={shareLogsLoading}
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: '学生姓名',
              dataIndex: 'student_name',
              key: 'student_name',
              width: 100
            },
            {
              title: '班级',
              dataIndex: 'class_name',
              key: 'class_name',
              width: 120
            },
            {
              title: '分享给',
              dataIndex: 'teacher_name',
              key: 'teacher_name',
              width: 100
            },
            {
              title: '分享人',
              dataIndex: 'shared_by_name',
              key: 'shared_by_name',
              width: 100,
              render: (text) => text || '管理员'
            },
            {
              title: '分享时间',
              dataIndex: 'shared_at',
              key: 'shared_at',
              width: 160,
              render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-'
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default Contacts; 
